import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const getScrollElement = () => {
	const siteBlocks = document.querySelector(".wp-site-blocks");

	if (!siteBlocks) return null;

	const overflowY = window.getComputedStyle(siteBlocks).overflowY;

	return overflowY === "auto" || overflowY === "scroll" ? siteBlocks : null;
};

const clearPostHeroScroll = (target) => {
	ScrollTrigger.getAll().forEach((trigger) => {
		if (trigger.vars?.id === "post-hero-scroll") {
			trigger.kill();
		}
	});

	gsap.set(target, { clearProps: "transform,opacity" });
};

const postHeroScroll = () => {
	const hero = document.querySelector(".single-post .post-hero");
	const target = hero?.querySelector(".post-hero__title");

	if (!hero || !target) return;

	clearPostHeroScroll(target);
	gsap.set(target, { opacity: 1, scale: 1, y: 0 });

	gsap.to(target, {
		scale: 0.78,
		opacity: 0,
		y: -24,
		transformOrigin: "right top",
		ease: "none",
		scrollTrigger: {
			id: "post-hero-scroll",
			trigger: hero,
			scroller: getScrollElement() || undefined,
			start: "top top",
			end: "bottom top",
			scrub: true,
			invalidateOnRefresh: true,
		},
	});
};

const postHeroTitlePreview = () => {
	const title = document.querySelector(".single-post .post-hero__title");
	const preview = document.querySelector(".single-post .post-hero__preview");
	const canHover = window.matchMedia(
		"(hover: hover) and (pointer: fine)",
	).matches;

	if (!title || !preview) return;

	const showPreview = () => {
		gsap.killTweensOf(preview);

		gsap.to(preview, {
			opacity: 1,
			scale: 1,
			duration: 0.3,
			ease: "power2.out",
		});
	};

	const hidePreview = () => {
		gsap.killTweensOf(preview);

		gsap.to(preview, {
			opacity: 0,
			scale: 0.85,
			duration: 0.25,
			ease: "power2.out",
		});
	};

	const movePreview = (event) => {
		gsap.to(preview, {
			x: event.clientX,
			y: event.clientY,
			duration: 0.25,
			ease: "power3.out",
		});
	};

	const centerPreviewOnTitle = () => {
		const titleRect = title.getBoundingClientRect();

		gsap.set(preview, {
			x: titleRect.left + titleRect.width / 2,
			y: titleRect.top + titleRect.height / 2,
		});
	};

	if (!canHover) {
		let isPreviewVisible = false;

		title.addEventListener("click", (event) => {
			event.stopPropagation();
			centerPreviewOnTitle();

			if (isPreviewVisible) {
				hidePreview();
				isPreviewVisible = false;
				return;
			}

			showPreview();
			isPreviewVisible = true;
		});

		document.addEventListener("click", () => {
			if (!isPreviewVisible) return;

			hidePreview();
			isPreviewVisible = false;
		});

		window.addEventListener("resize", () => {
			if (!isPreviewVisible) return;

			centerPreviewOnTitle();
		});

		return;
	}

	title.addEventListener("mouseenter", (event) => {
		movePreview(event);
		showPreview();
	});

	title.addEventListener("mouseleave", () => {
		hidePreview();
	});

	title.addEventListener("mousemove", movePreview);
};

const postSidebarImageReveal = () => {
	const image = document.querySelector(".single-post .post-sidebar__image");
	const isMobileLayout = window.matchMedia("(max-width: 991px)").matches;

	if (!image) return;

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				image.classList.add("is-visible");
				observer.unobserve(image);
			});
		},
		{
			root: getScrollElement(),
			rootMargin: isMobileLayout
				? "-20% 0px 0px 0px"
				: "-70% 0px 0px 0px",
			threshold: isMobileLayout ? 0.15 : 0.6,
		},
	);

	observer.observe(image);
};

const postSidebarStickyTitle = () => {
	const sidebar = document.querySelector(".single-post .post-sidebar");
	const wrap = document.querySelector(".single-post .post-content-wrap");

	if (!sidebar || !wrap) return;

	const media = gsap.matchMedia();

	media.add("(min-width: 992px)", () => {
		const trigger = ScrollTrigger.create({
			id: "post-sidebar-sticky-title",
			trigger: sidebar,
			endTrigger: wrap,
			scroller: getScrollElement() || undefined,
			start: "top 50px",
			end: () => `bottom ${sidebar.offsetHeight + 50}px`,
			onEnter: () => sidebar.classList.add("is-stuck"),
			onEnterBack: () => sidebar.classList.add("is-stuck"),
			onLeave: () => sidebar.classList.remove("is-stuck"),
			onLeaveBack: () => sidebar.classList.remove("is-stuck"),
			invalidateOnRefresh: true,
		});

		return () => {
			sidebar.classList.remove("is-stuck");
			trigger.kill();
		};
	});
};

const postBackToTopVisibility = () => {
	const hero = document.querySelector(".single-post .post-hero");
	const button = document.querySelector(".single-post .post-back-to-top");

	if (!hero || !button) return;

	ScrollTrigger.create({
		id: "post-back-to-top-visibility",
		trigger: hero,
		scroller: getScrollElement() || undefined,
		start: "bottom top",
		onEnter: () => button.classList.add("is-visible"),
		onLeaveBack: () => button.classList.remove("is-visible"),
		invalidateOnRefresh: true,
	});
};

export const singlePost = () => {
	if (!document.body.classList.contains("single-post")) return;
	const title = document.querySelector(".post-hero__title");
	const subtitle = document.querySelector("#sidebar-title");
	if (title && subtitle) {
		subtitle.innerText = title.innerText;
	}
	postHeroScroll();
	postHeroTitlePreview();
	postSidebarImageReveal();
	postSidebarStickyTitle();
	postBackToTopVisibility();
};
